import { createAction } from '@reduxjs/toolkit'
import {
  StructuredDataDisplayDTO,
  StructuredDataSchemaDTO,
  StructuredDataSchemaItem,
  StructuredDataType,
  StructuredDataValueDTO,
} from '@repo/shared/dtos/structured-data'
import { AsyncData, DataStatus } from '@repo/ui/utils'

import { RootState } from '@/store'
import { Beneficiary } from '@/store/beneficiary'
import { Organization } from '@/store/user'

type ActionPayload = (Record<string, unknown> & { generationId: string }) | void

// Implementation
export function createFakeAsyncThunk<StartActionPayload extends ActionPayload>(actionName: string) {
  return {
    start: createAction<StartActionPayload>(`${actionName}/pending`),
    end: createAction<StartActionPayload>(`${actionName}/fulfilled`),
  }
}

export type BeneficiaryStructuredDataViews = BeneficiaryStructuredDataView[]
export type BeneficiaryStructuredDataView = {
  id: string
  label: string
  items: {
    id: string
    label: string
    type: StructuredDataType
    value: StructuredDataSchemaItem['defaultValue']
  }[]
}
export const buildBeneficiaryStructuredDataView = ({
  display,
  schema,
  values,
}: {
  display: StructuredDataDisplayDTO
  schema: StructuredDataSchemaDTO
  values: StructuredDataValueDTO
}): BeneficiaryStructuredDataViews => {
  // const unplacedItemsSection = {
  //   id: UNPLACED_SECTION_ID,
  //   label: 'Unplaced Items',
  //   structuredDataIds: schema
  //     .map((item) => item.id)
  //     .filter((id) => !display.some((section) => section.structuredDataIds.includes(id))),
  // } satisfies StructuredDataSectionDisplay

  return display
    .map(
      (section) =>
        ({
          id: section.id,
          label: section.label,
          items: section.structuredDataIds
            .map((id) => {
              const label = schema.find((s) => s.id === id)?.label
              const value = values.find((s) => s.schemaItemId === id)?.value
              const type = schema.find((s) => s.id === id)?.type
              if (!label || !type) return null
              return {
                id,
                label,
                type,
                value: value ?? null,
              } satisfies BeneficiaryStructuredDataView['items'][0]
            })
            .filter((i) => i !== null),
        }) satisfies BeneficiaryStructuredDataView,
    )
    .filter((section) => section.items.length > 0) // Filter out empty sections
}

export const getCurrentBeneficiary = (state: RootState): AsyncData<Beneficiary> => {
  const currentBeneficiaryId = state.beneficiary.currentBeneficiaryId
  if (!currentBeneficiaryId) return DataStatus.Uninitialized

  const beneficiaries = state.beneficiary.all
  if (DataStatus.isUninitialized(beneficiaries)) return DataStatus.Uninitialized
  if (DataStatus.isLoading(beneficiaries)) return DataStatus.Loading
  if (DataStatus.isFulfilled(beneficiaries) && beneficiaries.length > 0) {
    return beneficiaries.find((b) => b.id === currentBeneficiaryId) || DataStatus.Error
  }
  return DataStatus.Error
}
export const getCurrentOrganization = (state: RootState): AsyncData<Organization> => {
  const currentOrganizationId = state.user.currentOrganizationId
  if (!currentOrganizationId) return DataStatus.Uninitialized

  const organizations = state.user.organizations
  if (DataStatus.isUninitialized(organizations)) return DataStatus.Uninitialized
  if (DataStatus.isLoading(organizations)) return DataStatus.Loading
  if (DataStatus.isFulfilled(organizations) && organizations.length > 0) {
    return organizations.find((o) => o.id === currentOrganizationId) || DataStatus.Uninitialized
  }
  return DataStatus.Error
}
